import React, { useEffect, useId, useRef, useState } from 'react';
import { Check, ChevronDown, Moon, Sun } from 'lucide-react';
import { useTheme } from '../hooks/useTheme';
import { useTranslation } from 'react-i18next';

const ThemeSwitcher = () => {
  const { setTheme, isDark, THEMES } = useTheme();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const itemRefs = useRef([]);
  const menuId = useId();

  const options = [
    { value: THEMES.DARK, label: t('common.themeDark'), Icon: Moon },
    { value: THEMES.LIGHT, label: t('common.themeLight'), Icon: Sun },
  ];
  const selectedTheme = isDark ? THEMES.DARK : THEMES.LIGHT;

  useEffect(() => {
    const handleClick = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (!open) return;
    const currentIndex = selectedTheme === THEMES.LIGHT ? 1 : 0;
    itemRefs.current[currentIndex >= 0 ? currentIndex : 0]?.focus();
  }, [open, selectedTheme, THEMES.LIGHT]);

  const closeMenu = (restoreFocus = false) => {
    setOpen(false);
    if (restoreFocus) {
      triggerRef.current?.focus();
    }
  };

  const handleTriggerKeyDown = (event) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    setOpen(true);
  };

  const handleMenuKeyDown = (event) => {
    const activeIndex = itemRefs.current.findIndex((item) => item === document.activeElement);
    if (event.key === 'Escape') {
      event.preventDefault();
      closeMenu(true);
      return;
    }

    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      itemRefs.current[event.key === 'Home' ? 0 : options.length - 1]?.focus();
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const offset = event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = (activeIndex + offset + options.length) % options.length;
    itemRefs.current[nextIndex]?.focus();
  };

  const CurrentIcon = isDark ? Moon : Sun;

  return (
    <div ref={ref} className="theme-switcher">
      <button
        type="button"
        ref={triggerRef}
        className="theme-button"
        onClick={() => setOpen((value) => !value)}
        onKeyDown={handleTriggerKeyDown}
        aria-label={t('common.switchTheme')}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={menuId}
        title={t('common.switchTheme')}
      >
        <CurrentIcon size={16} strokeWidth={2} aria-hidden="true" />
        <ChevronDown className="theme-button__chevron" size={13} strokeWidth={2} aria-hidden="true" />
      </button>

      {open && (
        <div
          id={menuId}
          className="dropdown-panel theme-menu"
          role="listbox"
          aria-label={t('common.switchTheme')}
          onKeyDown={handleMenuKeyDown}
        >
          {options.map(({ value, label, Icon }, index) => (
            <button
              key={value}
              ref={(element) => { itemRefs.current[index] = element; }}
              type="button"
              role="option"
              aria-selected={selectedTheme === value}
              className={`dropdown-panel__item${selectedTheme === value ? ' is-active' : ''}`}
              onClick={() => { setTheme(value); closeMenu(true); }}
            >
              <Icon size={16} strokeWidth={2} aria-hidden="true" />
              <span style={{ flex: 1 }}>{label}</span>
              {selectedTheme === value && <Check size={14} strokeWidth={2.5} aria-hidden="true" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ThemeSwitcher;
